import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { ethers } from 'ethers'

import { styled } from '@mui/material/styles'
import { InputBaseComponentProps } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import IconMinus from '@mui/icons-material/Remove'
import IconPlus from '@mui/icons-material/Add'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import useDApp from 'contexts/Web3'
import useGameEngine from 'contexts/GameEngine'
import Bets, { BET_BUTTON_AMOUNTS } from 'components/Bets'
import FightButton from 'components/FightButton'
import sleep from 'utils/sleep'

import './fighting-panel.scss'
import { ArenaGladiator } from 'scenes/Arena'

const debug = require('debug')('app:FightingPanel')

const Input = styled(OutlinedInput)({
  background: '#F1E4D8',

  '&.MuiInputBase-adornedStart': { paddingLeft: 4 },
  '&.MuiInputBase-adornedEnd': { paddingRight: 4 },

  '&.input-fight-times .MuiOutlinedInput-input': { textAlign: 'center' },

  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: 4,
    borderWidth: 0,
  },

  '&.Mui-disabled': {
    opacity: 0.6,
  },
})

const Adornment = styled(InputAdornment)({
  cursor: 'pointer',
  color: '#683100',

  '.Mui-disabled &': { opacity: 0.3 },
})

const NumberInput: InputBaseComponentProps = {
  inputMode: 'numeric',
  pattern: '[0-9]*',
}

const MAX_FIGHT_TIMES = 3000

enum FightPanelMessages {
  Start = 'Choose your bet',
  Fighting = 'Fighting...',
  AutoFights = 'Auto fighting...',
  LeftWin = 'Planet gladiator wins!',
  RightWin = 'Thunder gladiator wins!',
  Draw = 'Draw!',
  Error = 'Oops, something went wrong',
}

const FIGHT_BUTTON_STATE: Map<
  ArenaGladiator,
  'choose-sword' | 'choose-hammer' | null
> = new Map([
  [ArenaGladiator.None, null],
  [ArenaGladiator.Left, 'choose-sword'],
  [ArenaGladiator.Right, 'choose-hammer'],
])

const WINNER_TITLE: Map<0 | 1 | 2, string> = new Map([
  [0, '🪐 Wins'],
  [1, '⚡ Wins'],
  [2, '⚔ Draw'],
])

interface Summary {
  starts: number
  ends: number

  fights: number
  win: number
  lose: number
  draw: number

  paid: number
  earned: {
    TT: number
    PMT: number
  }

  error: Error | null
  show: boolean
  auto?: boolean
}

const initSummary: Summary = {
  starts: 0,
  ends: 0,

  fights: 0,
  win: 0,
  lose: 0,
  draw: 0,

  paid: 0,
  earned: {
    TT: 0,
    PMT: 0,
  },

  error: null,
  show: false,
}

type GameState = 'pending' | 'loading' | 'done' | 'error'

export default function FightingPanel() {
  const [initialized, init] = useState<boolean>(false)
  const [fightTimes, setFightTimes] = useState<number>(1)
  const [betSize, setBetSize] = useState<number>(0)
  const [betSizeText, setBetSizeText] = useState<string>('')
  const [message, setMessage] = useState<string>(FightPanelMessages.Start)

  const [gameState, setGameState] = useState<GameState>('pending')

  const [summary, setSummary] = useState<Summary>(initSummary)
  const {
    actions,
    wallet: { balance },
    balances: { TT },
    knight: { maxBet },
  } = useDApp()
  const engine = useGameEngine()

  const disabled = gameState === 'loading' || summary.auto

  const tt = Number(ethers.utils.formatEther(TT?.amount ?? balance ?? 0))

  const handleFightTimesChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setFightTimes(
        Math.max(1, Math.min(MAX_FIGHT_TIMES, parseInt(ev.target.value, 10)))
      )

      if (gameState !== 'pending') {
        setGameState('pending')
        engine!.actions.reset()
      }
    },
    [setFightTimes, gameState, engine]
  )

  const setBet = useCallback(
    (value: string) => {
      setBetSize(Number(value))
      setBetSizeText(value)

      if (gameState !== 'pending') {
        setGameState('pending')
        engine!.actions.reset()
      }
    },
    [gameState, engine]
  )

  const betButtonHandlers = useMemo(
    () => BET_BUTTON_AMOUNTS.map((amount) => () => setBet(amount)),
    [setBet]
  )

  const handleBetSizeChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = ev.target
      const betNumber = Number(value)

      if (!Number.isNaN(betNumber) && betNumber >= 0) setBet(value)
    },
    [setBet]
  )

  const selectAll = useCallback((ev: React.FocusEvent<HTMLInputElement>) => {
    ev.target.select()
  }, [])

  const setMaxBet = useCallback(
    () => setBet(Math.min(maxBet, tt).toFixed(0)),
    [maxBet, setBet, tt]
  )

  const plusFightTimes = useCallback(
    () => setFightTimes((prev) => Math.min(prev + 1, MAX_FIGHT_TIMES)),
    []
  )

  const minusFightTimes = useCallback(
    () => setFightTimes((prev) => Math.max(prev - 1, 1)),
    []
  )

  const startNextFighting = useCallback(async (last = summary) => {
    if (gameState !== 'pending') await engine!.actions.reset()
    if (
      engine?.selected !== ArenaGladiator.Left &&
      engine?.selected !== ArenaGladiator.Right
    )
      return

    if (!(Number(betSize) >= 5)) return

    const startingSummary = {
      ...last,
      starts: summary.starts ? summary.starts : Date.now(),
      paid: summary.paid + Number(betSize),
    }
    setSummary(startingSummary)
    setGameState('loading')
    setMessage(
      startingSummary.auto
        ? FightPanelMessages.AutoFights + ` (${summary.fights + 1} / ${fightTimes})`
        : FightPanelMessages.Fighting
    )
    engine!.actions.fight()

    actions
      .play(engine.selected, betSize)
      .then(
        ({
          won,
          winner,
          paid,
          earnedTT,
          earnedPMT,
        }: {
          won: boolean
          winner: 0 | 1 | 2
          paid: string
          earnedTT: string
          earnedPMT: string
        }) => {
          debug(
            '[%s][%s] paid: %s TT >> earned: %s TT | %s PMT',
            won ? '🏆' : '💀',
            WINNER_TITLE.get(winner),
            paid,
            earnedTT,
            earnedPMT
          )

          switch (winner) {
            case 0:
              engine!.actions.announceGladiator1Win()
              setMessage(FightPanelMessages.LeftWin)
              break
            case 1:
              engine!.actions.announceGladiator2Win()
              setMessage(FightPanelMessages.RightWin)
              break
            case 2:
              engine!.actions.announceDraw()
              setMessage(FightPanelMessages.Draw)
              break
            default:
              console.warn(`Unknown winner: ${winner}`)
              engine!.actions.reset()
              break
          }

          return sleep(2500).then(() => {
            setGameState('done')
            setSummary({
              ...startingSummary,
              ends: Date.now(),
              fights: startingSummary.fights + 1,

              win: startingSummary.win + (won ? 1 : 0),
              lose: startingSummary.lose + (!won ? 1 : 0),
              draw: startingSummary.draw + (winner === 2 ? 1 : 0),

              earned: {
                TT: startingSummary.earned.TT + (Number(earnedTT) || 0),
                PMT: startingSummary.earned.PMT + (Number(earnedPMT) || 0),
              },

              show: true,
            })
          })
        }
      )
      .catch((error: Error) => {
        console.error(error)
        actions.showError(error.message ?? error.toString())
        setGameState('error')
        setSummary({
          ...startingSummary,
          ends: Date.now(),
          paid: summary.paid,
          error,
          auto: false,
        })
        setMessage(FightPanelMessages.Error)
        return engine!.actions.reset()
      })
      .then(actions.refreshGameInfo)
  }, [gameState, engine, betSize, summary, fightTimes, actions])

  const handleFightButtonClick = useCallback(() => startNextFighting({
    ...initSummary,
    auto: fightTimes > 1,
  }), [startNextFighting, fightTimes])

  // Auto Max Bet
  useEffect(() => {
    if (!initialized && tt) {
      debug('[AutoBet] betSize: %d | TT: %d', betSize, tt)
      const defaultAmount = Number(
        [...BET_BUTTON_AMOUNTS] // 這邊要 clone array 避免影響原始 array
          .sort((a, b) => Number(b) - Number(a))
          .find((amount) => tt >= Number(amount)) ?? 0
      )

      debug('[AutoBet] found reasonable auto bet amount: %d', defaultAmount)
      if (defaultAmount > 0) {
        setBet(`${defaultAmount}`)
        init(true)
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tt, initialized, setBet])

  // Auto Next Round
  useEffect(() => {
    if (summary.auto) {
      if (gameState === 'error') {
        setSummary({ ...summary, auto: false})
      }

      if (gameState === 'done') {
        if (summary.fights < fightTimes) {
          startNextFighting()
        } else {
          setSummary({ ...summary, auto: false})
        }
      }
    }
    // 這邊故意只判斷已戰鬥場數, 避免多次觸發
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary.fights])

  const autoReset = useMemo(() => {
    if (gameState !== 'pending' && gameState !== 'loading' && !summary.auto) {
      return () => {
        setSummary(initSummary)
        setGameState('pending')
        setMessage(FightPanelMessages.Start)
        engine?.actions.reset()
      }
    }
  }, [engine?.actions, gameState, summary.auto])

  return (
    <Box
      className="fighting-panel"
      width="100%"
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      zIndex={10}
      onMouseDown={autoReset}
    >
      {/* Empty Block */}
      <Box width="100%" height={320} />
      <Stack
        direction="row"
        height={60}
        justifyContent="center"
        alignItems="center"
      >
        <Typography variant="FightPanel">{message}</Typography>
      </Stack>
      <Stack
        className="bet-amount-selection"
        direction="row"
        spacing={0}
        height={55}
        justifyContent="center"
        alignItems="center"
      >
        {BET_BUTTON_AMOUNTS.map((amount, i) => (
          <Bets
            key={amount}
            amount={amount}
            size="small"
            selected={Number(amount) === betSize}
            disabled={disabled}
            onClick={betButtonHandlers[i]}
          />
        ))}
      </Stack>

      {/* Bet Size */}
      <Stack px={4} spacing={2} direction="row" alignItems="center" mt={3}>
        <Typography
          variant="FightPanel"
          flexBasis="34%"
          flexGrow={0}
          flexShrink={0}
          textAlign="right"
        >
          Bet size
        </Typography>

        <Box flexGrow={1}>
          <Input
            className="input-bet-size"
            placeholder="Minumum 10"
            value={betSizeText}
            color="secondary"
            fullWidth
            size="small"
            inputProps={NumberInput}
            disabled={disabled}
            onChange={handleBetSizeChange}
            onFocus={selectAll}
            endAdornment={
              <Adornment position="end">
                <Button
                  color="inherit"
                  onClick={setMaxBet}
                  disabled={disabled}
                  sx={{ minWidth: 48, fontWeight: 600 }}
                >
                  MAX
                </Button>
              </Adornment>
            }
          />
        </Box>
      </Stack>
      {/* /Bet Size */}

      {/* Fight Times */}
      <Stack px={4} spacing={2} direction="row" alignItems="center" mt={3}>
        <Typography
          variant="FightPanel"
          flexBasis="34%"
          flexGrow={0}
          flexShrink={0}
          textAlign="right"
        >
          Fight times
        </Typography>

        <Box flexGrow={1}>
          <Input
            className="input-fight-times"
            value={fightTimes}
            color="secondary"
            fullWidth
            size="small"
            inputProps={NumberInput}
            disabled={disabled}
            onChange={handleFightTimesChange}
            onFocus={selectAll}
            startAdornment={
              <Adornment position="start">
                <IconButton
                  size="small"
                  color="inherit"
                  disabled={disabled}
                  onClick={minusFightTimes}
                >
                  <IconMinus color="inherit" />
                </IconButton>
              </Adornment>
            }
            endAdornment={
              <Adornment position="end">
                <IconButton
                  size="small"
                  color="inherit"
                  disabled={disabled}
                  onClick={plusFightTimes}
                >
                  <IconPlus color="inherit" />
                </IconButton>
              </Adornment>
            }
          />
        </Box>
      </Stack>
      {/* /Fight Times */}

      {/* Fight Button */}
      <Stack mt={6} mb={8} height={130} alignItems="center" justifyItems="center">
        <FightButton
          size={130}
          state={FIGHT_BUTTON_STATE.get(engine!.selected)}
          disabled={
            disabled ||
            engine?.selected === ArenaGladiator.None
          }
          fighting={gameState === 'loading'}
          onClick={handleFightButtonClick}
        >
          { engine?.selected === ArenaGladiator.None ? 'Choose Gladiator' : 'FIGHT' }
        </FightButton>
      </Stack>
      {/* /Fight Button */}

      {/* {process.env.REACT_APP_DEBUG ? (
        <Box position="fixed" right={10} bottom={10}>
          <Box>DEBUG Panel</Box>
          <Box>Selected: {engine?.selected}</Box>
          <ButtonGroup variant="contained" size="small" color="warning">
            <Button onClick={engine!.actions.fight}>fight</Button>
            <Button onClick={engine!.actions.announceGladiator1Win}>
              left win
            </Button>
            <Button onClick={engine!.actions.announceGladiator2Win}>
              right win
            </Button>
            <Button onClick={engine!.actions.announceDraw}>draw</Button>
            <Button onClick={engine!.actions.reset}>reset</Button>
          </ButtonGroup>
        </Box>
      ) : null} */}
    </Box>
  )
}
